import { ScannerKeyResolution } from "./scannerKeyNormalization";
import { captureScannerTrace } from "./scannerSentryDiagnostics";
import { hasBarcodeIdentifier } from "./scannerTraceClassification";

export const RAW_SCANNER_CONSOLE_PREFIX = "[BarcodeScannerRaw]";
export const RAW_SCANNER_DIAGNOSTICS_ENABLED = true;
const MAX_TRACE_EVENTS = 500;
const MAX_TARGET_VALUE_LENGTH = 10000;

export interface RawScannerTarget {
  tag: string;
  id: string;
  name: string;
  type: string;
  placeholder: string;
  barcodeInput: string;
  contentEditable: boolean;
  valueLength: number | null;
  value: string;
}

export interface RawScannerEvent {
  sequence: number;
  eventType: string;
  occurredAt: string;
  eventTimeStamp: number;
  handlerTimeStamp: number;
  eventGapMs: number;
  handlerGapMs: number;
  key: string;
  code: string;
  keyCode: number;
  which: number;
  charCode: number;
  data: string | null;
  inputType: string;
  clipboardText: string;
  isTrusted: boolean;
  cancelable: boolean;
  defaultPrevented: boolean;
  isComposing: boolean;
  repeat: boolean;
  modifiers: string[];
  resolution: ScannerKeyResolution | null;
  decision: string;
  bufferBefore: string;
  bufferAfter: string;
  target: RawScannerTarget;
  activeElement: RawScannerTarget;
}

export interface RawScannerCaptureContext {
  event: Event;
  decision: string;
  bufferBefore: string;
  bufferAfter: string;
  resolution?: ScannerKeyResolution;
}

export interface RawScannerTracePayload {
  ownerKey: string;
  reason: string;
  rawBuffer: string;
  normalizedCandidate?: string | null;
  transformations?: string[];
  events: RawScannerEvent[];
  details?: Record<string, unknown>;
}

const getTextTargetValue = (target: HTMLElement) => {
  if (target instanceof HTMLInputElement && target.type === "password") {
    return "[excluded-password]";
  }
  if (target instanceof HTMLInputElement) {
    return target.value.slice(0, MAX_TARGET_VALUE_LENGTH);
  }
  if (target instanceof HTMLTextAreaElement) {
    return target.value.slice(0, MAX_TARGET_VALUE_LENGTH);
  }
  if (target.isContentEditable) {
    return (target.textContent || "").slice(0, MAX_TARGET_VALUE_LENGTH);
  }
  return "";
};

const getTextTargetValueLength = (target: HTMLElement) => {
  if (target instanceof HTMLInputElement && target.type === "password") {
    return 0;
  }
  if (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement
  ) {
    return target.value.length;
  }
  if (target.isContentEditable) {
    return (target.textContent || "").length;
  }
  return 0;
};

const isBarcodeTarget = (target: HTMLElement) =>
  target.dataset.barcodeScannerInput === "true" ||
  hasBarcodeIdentifier([
    target.dataset.barcodeScannerInput,
    target.id,
    target.getAttribute("name"),
    target.getAttribute("placeholder"),
  ]);

export const describeRawScannerTarget = (
  target: EventTarget | null,
  includeValue = false
): RawScannerTarget => {
  if (!(target instanceof HTMLElement)) {
    return {
      tag: "unknown",
      id: "",
      name: "",
      type: "",
      placeholder: "",
      barcodeInput: "",
      contentEditable: false,
      valueLength: 0,
      value: "",
    };
  }
  return {
    tag: target.tagName.toLowerCase(),
    id: target.id,
    name: target.getAttribute("name") || "",
    type: target.getAttribute("type") || "",
    placeholder: target.getAttribute("placeholder") || "",
    barcodeInput: target.dataset.barcodeScannerInput || "",
    contentEditable: target.isContentEditable,
    valueLength:
      target.isContentEditable && !includeValue
        ? null
        : getTextTargetValueLength(target),
    value: includeValue ? getTextTargetValue(target) : "",
  };
};

const getModifiers = (event: Event) => {
  if (!(event instanceof KeyboardEvent)) return [];
  return [
    event.ctrlKey && "Control",
    event.altKey && "Alt",
    event.shiftKey && "Shift",
    event.metaKey && "Meta",
  ].filter(Boolean) as string[];
};

const getInputData = (event: Event) => {
  if (!(event instanceof InputEvent)) {
    return { data: null, inputType: "" };
  }
  if (
    event.target instanceof HTMLInputElement &&
    event.target.type === "password"
  ) {
    return { data: null, inputType: event.inputType };
  }
  return { data: event.data, inputType: event.inputType };
};

const getClipboardText = (event: Event) => {
  if (event.type !== "paste" || !("clipboardData" in event)) return "";
  if (!(event.target instanceof HTMLInputElement)) return "";
  if (event.target.type === "password") return "";
  if (!isBarcodeTarget(event.target)) return "";
  return (event as ClipboardEvent).clipboardData?.getData("text") || "";
};

export const createRawScannerEvent = ({
  event,
  decision,
  bufferBefore,
  bufferAfter,
  resolution,
}: RawScannerCaptureContext): RawScannerEvent => {
  const keyboard = event instanceof KeyboardEvent ? event : null;
  const input = getInputData(event);
  const targetElement =
    event.target instanceof HTMLElement ? event.target : null;
  const target = describeRawScannerTarget(
    event.target,
    Boolean(targetElement && isBarcodeTarget(targetElement))
  );
  const activeElement =
    event.target === document.activeElement
      ? target
      : describeRawScannerTarget(document.activeElement);
  return {
    sequence: 0,
    eventType: event.type,
    occurredAt: new Date().toISOString(),
    eventTimeStamp: event.timeStamp,
    handlerTimeStamp: performance.now(),
    eventGapMs: 0,
    handlerGapMs: 0,
    key: keyboard?.key || "",
    code: keyboard?.code || "",
    keyCode: keyboard?.keyCode || 0,
    which: keyboard?.which || 0,
    charCode: keyboard?.charCode || 0,
    data: input.data,
    inputType: input.inputType,
    clipboardText: getClipboardText(event),
    isTrusted: event.isTrusted,
    cancelable: event.cancelable,
    defaultPrevented: event.defaultPrevented,
    isComposing: keyboard?.isComposing || false,
    repeat: keyboard?.repeat || false,
    modifiers: getModifiers(event),
    resolution: resolution || null,
    decision,
    bufferBefore,
    bufferAfter,
    target,
    activeElement,
  };
};

export const appendRawScannerEvent = (
  trace: RawScannerEvent[],
  event: RawScannerEvent
) => {
  if (!RAW_SCANNER_DIAGNOSTICS_ENABLED) return;
  const previous = trace[trace.length - 1];
  event.sequence = (previous?.sequence || 0) + 1;
  event.eventGapMs = Math.max(
    0,
    event.eventTimeStamp - (previous?.eventTimeStamp || event.eventTimeStamp)
  );
  event.handlerGapMs = Math.max(
    0,
    event.handlerTimeStamp -
      (previous?.handlerTimeStamp || event.handlerTimeStamp)
  );
  if (trace.length >= MAX_TRACE_EVENTS) {
    trace.splice(0, Math.floor(MAX_TRACE_EVENTS / 5));
  }
  trace.push(event);
};

export const logRawScannerEnvironment = (
  ownerKey: string,
  timing: Record<string, unknown>
) => {
  if (!RAW_SCANNER_DIAGNOSTICS_ENABLED) return;
  const environment = {
    reason: "listener_mounted",
    ownerKey,
    occurredAt: new Date().toISOString(),
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    languages: navigator.languages,
    route: window.location.href,
    release: process.env.REACT_APP_SENTRY_RELEASE || "",
    registeredEventSources: ["keydown", "beforeinput", "input", "paste"],
    timing,
  };
  // Temporary production diagnostic requested for the on-site scanner test.
  // eslint-disable-next-line no-console
  console.info(RAW_SCANNER_CONSOLE_PREFIX, environment);
  // eslint-disable-next-line no-console
  console.info(
    `${RAW_SCANNER_CONSOLE_PREFIX}:json`,
    JSON.stringify(environment)
  );
};

export const logRawScannerTrace = (payload: RawScannerTracePayload) => {
  if (!RAW_SCANNER_DIAGNOSTICS_ENABLED) return;
  const trace = {
    reason: payload.reason,
    ownerKey: payload.ownerKey,
    occurredAt: new Date().toISOString(),
    rawBuffer: payload.rawBuffer,
    normalizedCandidate: payload.normalizedCandidate ?? null,
    transformations: payload.transformations || [],
    events: payload.events,
    ...payload.details,
  };
  // This deliberately includes raw barcode/input data for the controlled test.
  // eslint-disable-next-line no-console
  console.info(RAW_SCANNER_CONSOLE_PREFIX, trace);
  // eslint-disable-next-line no-console
  console.info(`${RAW_SCANNER_CONSOLE_PREFIX}:json`, JSON.stringify(trace));
  captureScannerTrace(payload);
};
